import React from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { ExportOutlined } from '@ant-design/icons';
import { App, Button, Card, Typography, Divider } from 'antd';

import { NCRoamAgencySubscription } from './models';

import * as actions from './store/actions';

import { apiOptions } from '../../shared/constants';

import { isDarkTheme } from '../../shared/utils';

import lightLogo from '../../assets/svg/logo_big_light_theme.svg';
import darkLogo from '../../assets/svg/logo_big_dark_theme.svg';

import { AppDispatch } from '../../store';

const { Title, Text } = Typography;

export type NCRoamSubscriptionSummaryComponentProps = {
  subscriptionInfo: NCRoamAgencySubscription | null;
  refresh: Function;
};

const NCRoamSubscriptionSummaryComponent = ({ subscriptionInfo, refresh }: NCRoamSubscriptionSummaryComponentProps) => {
  const { modal } = App.useApp();

  const dispatch = useDispatch<AppDispatch>();

  const handleCancelSubscriptionClick = () => {
    modal.confirm({
      title: 'Notice',
      icon: null,
      content: 'Are you sure you want to cancel your NC Roam subscription?',
      okText: 'Confirm',
      onOk() {
        return new Promise((resolve, reject) => {
          dispatch(actions.ncRoamCancelSubscription()).then(() => {
            resolve(null);

            refresh();
          });
        });
      },
    });
  };

  const logoUrl = isDarkTheme() ? `${apiOptions.appAssetsRootPath}${darkLogo}` : `${apiOptions.appAssetsRootPath}${lightLogo}`;

  return (
    <>
      <Card size="small" style={{ maxWidth: 'fit-content', minWidth: '550px', marginLeft: 'auto', marginRight: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div className="nc-roam-subscription-page-logo" style={{ backgroundImage: `url(${logoUrl})` }} />

          <iframe
            style={{ width: '800px', height: '400px' }}
            frameBorder="0"
            title="Youtube player"
            sandbox="allow-same-origin allow-forms allow-popups allow-scripts allow-presentation"
            src={`https://youtube.com/embed/Bm7C6Dx5YH4?autoplay=0`}
          ></iframe>

          <Title level={2} style={{ margin: '15px 0 0 0', fontWeight: 'bold' }}>
            NC Roam is a mobile application for NowCerts users
          </Title>

          <Title level={5} style={{ margin: '1em 0 1em 0' }}>
            "Perfect for the long weekends... - access NowCerts from your phone!"
          </Title>

          <Button
            type="primary"
            shape="round"
            target="_blank"
            href="https://ncroam.com"
            style={{ fontWeight: 'bold', background: '#2BC8F3', borderColor: '#2BC8F3', boxShadow: 'none' }}
          >
            Learn more. <ExportOutlined />
          </Button>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '25px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Text style={{ fontWeight: '500' }}>Subscription Plan</Text>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: '18px' }}>
                {subscriptionInfo && subscriptionInfo.hasSubscription
                  ? subscriptionInfo.name
                  : subscriptionInfo && subscriptionInfo.trialEndsAt
                  ? 'In trial'
                  : 'Not active'}
              </Text>

              {subscriptionInfo && subscriptionInfo.hasSubscription ? (
                <Button type="link" onClick={handleCancelSubscriptionClick}>
                  Cancel
                </Button>
              ) : null}
            </div>
          </div>

          {subscriptionInfo && subscriptionInfo.trialEndsAt ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Text style={{ fontWeight: '500' }}>Trial ends at</Text>

              <Text type="secondary" style={{ fontSize: '18px' }}>
                {moment(subscriptionInfo.trialEndsAt).format('MM/DD/YYYY')}
              </Text>
            </div>
          ) : null}

          {subscriptionInfo && subscriptionInfo.hasSubscription ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Text style={{ fontWeight: '500' }}>Max Active Users</Text>

              <Text type="secondary" style={{ fontSize: '18px' }}>
                {subscriptionInfo.maxUsers}
              </Text>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Text style={{ fontWeight: '500' }}>Active Users</Text>

            <Text type="secondary" style={{ fontSize: '18px', color: subscriptionInfo && subscriptionInfo.exceedsPlanSize ? '#cf1322' : '' }}>
              {subscriptionInfo ? subscriptionInfo.totalActiveUsers : 0}
            </Text>
          </div>

          {subscriptionInfo && subscriptionInfo.hasSubscription ? (
            <>
              {subscriptionInfo && subscriptionInfo.hasSubscription && subscriptionInfo.nextPaymentDate ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Text style={{ fontWeight: '500' }}>Next Payment Date</Text>

                  <Text type="secondary" style={{ fontSize: '18px' }}>
                    {moment(subscriptionInfo.nextPaymentDate).format('MM/DD/YYYY')}
                  </Text>
                </div>
              ) : null}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Text style={{ fontWeight: '500' }}>Monthly Price</Text>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '18px' }}>
                    {`$${subscriptionInfo.monthlyPrice}`}
                  </Text>

                  {subscriptionInfo.customerPortalUrl ? (
                    <Button type="link" style={{ marginLeft: '10px' }} target="_blank" href={subscriptionInfo.customerPortalUrl}>
                      Stripe Customer Portal
                    </Button>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </Card>
    </>
  );
};

export default NCRoamSubscriptionSummaryComponent;
